import type { MetaDescriptor } from "react-router";

export const SITE_ORIGIN = "https://truspecta.com";
export const SITE_DESCRIPTION =
  "Truspecta helps privacy teams keep up with constant change by connecting reviews, findings, drift, and evidence to real systems.";

const DEFAULT_OG_IMAGE = `${SITE_ORIGIN}/og.png`;
const OG_IMAGE_WIDTH = "1200";
const OG_IMAGE_HEIGHT = "630";

export interface RouteMetaInput {
  title: string;
  description: string;
  /** Pathname including leading slash. Template literal type catches missing-slash typos at compile time. */
  path: `/${string}`;
  ogImage?: string;
  ogType?: "website" | "article";
}

const FOUNDER_NAME = "Nathan Lemay";
const CONTACT_EMAIL = "hello@truspecta.com";

export function buildOrganizationSchema(opts: { sameAs?: readonly string[] } = {}) {
  const base: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "Truspecta",
    url: SITE_ORIGIN,
    description: SITE_DESCRIPTION,
    logo: {
      "@type": "ImageObject",
      url: `${SITE_ORIGIN}/og.png`,
      width: 1200,
      height: 630,
    },
    contactPoint: [
      {
        "@type": "ContactPoint",
        contactType: "customer support",
        email: CONTACT_EMAIL,
        availableLanguage: ["en"],
      },
    ],
    founder: {
      "@type": "Person",
      name: FOUNDER_NAME,
    },
  };
  if (opts.sameAs && opts.sameAs.length > 0) {
    base.sameAs = opts.sameAs;
  }
  return base;
}

export function buildWebSiteSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "Truspecta",
    url: SITE_ORIGIN,
  };
}

export function buildSoftwareApplicationSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: "Truspecta",
    applicationCategory: "BusinessApplication",
    operatingSystem: "Web",
    description: "Truspecta anchors privacy reviews, findings, drift, and evidence to the workloads they belong to.",
    url: `${SITE_ORIGIN}/product`,
    provider: { "@type": "Organization", name: "Truspecta", url: SITE_ORIGIN },
    offers: { "@type": "Offer", availability: "https://schema.org/PreOrder" },
  };
}

export function buildFounderPersonSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "Person",
    name: FOUNDER_NAME,
    jobTitle: "Founder",
    worksFor: { "@type": "Organization", name: "Truspecta", url: SITE_ORIGIN },
  };
}

export function buildRouteMeta(input: RouteMetaInput): MetaDescriptor[] {
  const { title, description, path, ogImage = DEFAULT_OG_IMAGE, ogType = "website" } = input;
  if (!path.startsWith("/")) throw new Error(`buildRouteMeta: path must start with "/", got "${path}"`);
  const url = path === "/" ? `${SITE_ORIGIN}/` : `${SITE_ORIGIN}${path}`;

  return [
    { title },
    { name: "description", content: description },
    { property: "og:type", content: ogType },
    { property: "og:locale", content: "en_US" },
    { property: "og:site_name", content: "Truspecta" },
    { property: "og:title", content: title },
    { property: "og:description", content: description },
    { property: "og:url", content: url },
    { property: "og:image", content: ogImage },
    { property: "og:image:width", content: OG_IMAGE_WIDTH },
    { property: "og:image:height", content: OG_IMAGE_HEIGHT },
    { property: "og:image:alt", content: description },
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:title", content: title },
    { name: "twitter:description", content: description },
    { name: "twitter:image", content: ogImage },
    { tagName: "link", rel: "canonical", href: url },
    { name: "robots", content: "index, follow" },
    { name: "theme-color", content: "#2F6B63" },
  ];
}
